import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { WORKFLOW_RUNTIME_CONFIG } from "@los-workspace/workflow-runtime";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../../environments/environment";
import { AuthService } from "../../../services/auth.service";
import type { PaginationMetadata } from "../models/opportunity.model";
import { coerceText } from "../utils/coerce-text";
import { isDirectSourceType } from "../utils/opportunity-direct-source.util";
import {
  extractPaginatedCollection,
  extractPaginationMetadata,
  unwrapApiPayload,
} from "./api-response.util";

export type PackQueryItem = { key?: string; value?: string; enabled?: boolean };

export type PackQueryParamValue = string | number | boolean | undefined;

export type PackDataSourceVars = Record<string, PackQueryParamValue>;

export type PackFetchOptionsResult = {
  options: Array<{ label: string; value: string }>;
  attributesByValue: Record<string, Record<string, unknown>>;
};

export type PackFetchOptionsPageResult = PackFetchOptionsResult & {
  metadata: PaginationMetadata;
};

/**
 * Executes workflow/form dataSource + optionsConfig HTTP calls using the
 * endpoint, method, query, headers, and responseMapping defined in the pack JSON.
 */
@Injectable({ providedIn: "root" })
export class PackDataSourceService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly runtimeConfig = inject(WORKFLOW_RUNTIME_CONFIG, {
    optional: true,
  });

  private get apiUrl(): string {
    return (this.runtimeConfig?.apiUrl ?? environment.apiUrl).replace(
      /\/$/,
      "",
    );
  }

  /** Always POST to workflow-runtime/proxy — never call reference-data directly from the browser. */
  private async callWorkflowProxy<T>(req: {
    method?: string;
    endpoint: string;
    query?: PackDataSourceVars;
    headers?: Record<string, string>;
    body?: unknown;
  }): Promise<T> {
    const res = await firstValueFrom(
      this.http.post<{ success?: boolean; data?: T } | T>(
        `${this.apiUrl}/workflow-runtime/proxy`,
        {
          method: req.method ?? "GET",
          endpoint: req.endpoint,
          query: req.query,
          headers: req.headers,
          body: req.body,
        },
      ),
    );
    if (res && typeof res === "object" && "data" in (res as object)) {
      return (res as { data: T }).data;
    }
    return unwrapApiPayload<T>(res);
  }

  async fetch(
    dataSource: Record<string, unknown> | null | undefined,
    vars: PackDataSourceVars = {},
  ): Promise<unknown> {
    if (!dataSource) throw new Error("dataSource is required");
    const resolvedVars = this.enrichPackDataSourceVars(vars);
    const rawEndpoint = coerceText(dataSource["endpoint"] ?? "");
    const endpoint = normalizePackProxyEndpoint(rawEndpoint);
    if (!endpoint) throw new Error("dataSource.endpoint is required");
    assertResolvedPackEndpoint(endpoint);

    const method = coerceText(dataSource["method"] ?? "GET");
    const query = {
      ...extractPackEndpointQuery(rawEndpoint),
      ...buildPackQuery(dataSource["query"], resolvedVars),
    };
    normalizePackSourceDescriptionQuery(endpoint, query);

    return this.callWorkflowProxy({
      endpoint,
      method,
      query,
      body: dataSource["body"],
      headers: sanitizeHeaders(dataSource["headers"]),
    });
  }

  async fetchOptions(
    optionsConfig: Record<string, unknown> | null | undefined,
    vars: PackDataSourceVars = {},
  ): Promise<Array<{ label: string; value: string }>> {
    const result = await this.fetchOptionsWithAttributes(optionsConfig, vars);
    return result.options;
  }

  async fetchOptionsWithAttributes(
    optionsConfig: Record<string, unknown> | null | undefined,
    vars: PackDataSourceVars = {},
  ): Promise<PackFetchOptionsResult> {
    const page = await this.fetchOptionsPage(
      optionsConfig,
      vars,
      1,
      readPackQueryLimit(optionsConfig?.["query"]),
    );
    return {
      options: page.options,
      attributesByValue: page.attributesByValue,
    };
  }

  /**
   * Resolve a stored dropdown value to its display label using the same pack
   * optionsConfig + workflow proxy path as create/edit forms.
   */
  async resolvePackOptionLabel(
    optionsConfig: Record<string, unknown> | null | undefined,
    vars: PackDataSourceVars,
    storedValue: string,
  ): Promise<string | undefined> {
    const needle = storedValue.trim();
    if (!optionsConfig || !needle) {
      return undefined;
    }

    const staticOpts = Array.isArray(optionsConfig["options"])
      ? (optionsConfig["options"] as Array<{
          label?: string;
          value?: string;
          active?: boolean;
        }>)
      : null;
    if (staticOpts?.length || optionsConfig["source"] === "static") {
      const match = (staticOpts ?? []).find(
        (option) =>
          option.active !== false &&
          (String(option.value ?? "") === needle ||
            String(option.label ?? "") === needle),
      );
      return match?.label ? String(match.label) : undefined;
    }

    const mapping = normalizePackOptionsResponseMapping(
      coerceText(optionsConfig["endpoint"] ?? ""),
      (optionsConfig["responseMapping"] as Record<string, unknown>) ?? {},
    );
    const labelKey = coerceText(mapping["labelKey"] ?? "label");
    const valueKey = coerceText(mapping["valueKey"] ?? "code");
    const collectionPath = coerceText(mapping["collectionPath"] ?? "data");

    let page = 1;
    let hasMore = true;
    const limit = 100;

    while (hasMore) {
      const raw = await this.fetchOptionsRawPage(
        optionsConfig,
        vars,
        page,
        limit,
      );
      const rows = extractPaginatedCollection(raw, collectionPath);
      for (const row of rows) {
        const record = row as Record<string, unknown>;
        const candidates = [
          coerceText(record[valueKey]),
          coerceText(record["code"]),
          coerceText(record["id"]),
        ].filter(Boolean);
        if (candidates.includes(needle)) {
          const label = coerceText(
            record[labelKey] ??
              record["label"] ??
              record["name"] ??
              record["description"] ??
              "",
          );
          if (label) {
            return label;
          }
        }
      }

      const metadata = extractPaginationMetadata(raw, rows.length);
      hasMore = metadata.hasMore;
      page += 1;
    }

    return undefined;
  }

  /** Resolve a stored dropdown value to its master record id (UUID). */
  async resolvePackOptionRecordId(
    optionsConfig: Record<string, unknown> | null | undefined,
    vars: PackDataSourceVars,
    storedValue: string,
  ): Promise<string | undefined> {
    const needle = storedValue.trim();
    if (!optionsConfig || !needle) {
      return undefined;
    }

    if (isUuid(needle)) {
      return needle;
    }

    const staticMatch = this.resolveStaticPackOptionRecordId(optionsConfig, needle);
    if (staticMatch !== null) {
      return staticMatch;
    }

    const mapping = normalizePackOptionsResponseMapping(
      coerceText(optionsConfig['endpoint'] ?? ''),
      (optionsConfig['responseMapping'] as Record<string, unknown>) ?? {},
    );
    const valueKey = coerceText(mapping['valueKey'] ?? 'code');
    const collectionPath = coerceText(mapping['collectionPath'] ?? 'data');

    let page = 1;
    let hasMore = true;
    const limit = 100;

    while (hasMore) {
      const raw = await this.fetchOptionsRawPage(optionsConfig, vars, page, limit);
      const rows = extractPaginatedCollection(raw, collectionPath);
      for (const row of rows) {
        const record = row as Record<string, unknown>;
        const candidates = [
          coerceText(record[valueKey]),
          coerceText(record['code']),
          coerceText(record['id']),
        ].filter(Boolean);
        if (candidates.includes(needle)) {
          const id = coerceText(record['id']);
          if (id) {
            return id;
          }
        }
      }

      const metadata = extractPaginationMetadata(raw, rows.length);
      hasMore = metadata.hasMore;
      page += 1;
    }

    return undefined;
  }

  private resolveStaticPackOptionRecordId(
    optionsConfig: Record<string, unknown>,
    needle: string,
  ): string | undefined | null {
    const staticOpts = Array.isArray(optionsConfig['options'])
      ? (optionsConfig['options'] as Array<{
          label?: string;
          value?: string;
          active?: boolean;
        }>)
      : null;
    if (!staticOpts?.length && optionsConfig['source'] !== 'static') {
      return null;
    }

    const match = (staticOpts ?? []).find(
      (option) =>
        option.active !== false &&
        (String(option.value ?? '') === needle || String(option.label ?? '') === needle),
    );
    return match?.value ? String(match.value) : undefined;
  }

  private enrichPackDataSourceVars(
    vars: PackDataSourceVars,
  ): PackDataSourceVars {
    const enriched: PackDataSourceVars = { ...vars };
    const authUserId = this.auth.currentUser()?.id?.trim();
    if (authUserId && !coerceText(enriched["userId"]).trim()) {
      enriched["userId"] = authUserId;
    }
    return enriched;
  }

  private async fetchOptionsRawPage(
    optionsConfig: Record<string, unknown>,
    vars: PackDataSourceVars,
    page: number,
    limit: number,
  ): Promise<unknown> {
    const resolvedVars = this.enrichPackDataSourceVars(vars);
    const rawEndpoint = coerceText(optionsConfig["endpoint"] ?? "");
    let endpoint = normalizePackProxyEndpoint(rawEndpoint);
    endpoint = normalizePackSourceTypeEndpoint(rawEndpoint, endpoint);
    if (!endpoint) {
      return null;
    }

    const method = coerceText(optionsConfig["method"] ?? "GET");
    const jsonQuery = {
      ...extractPackEndpointQuery(rawEndpoint),
      ...buildPackQuery(optionsConfig["query"], resolvedVars),
    };
    const pageLimit = readPackQueryLimit(optionsConfig["query"], limit);
    const paramNames = readPackPaginationParamNames(
      optionsConfig["pagination"],
    );
    const query: Record<string, string> = {
      ...jsonQuery,
      [paramNames.page]: String(page),
      [paramNames.pageSize]: String(pageLimit),
    };
    normalizePackSourceDescriptionQuery(endpoint, query);

    const body = buildPackOptionsProxyBody(
      optionsConfig,
      query,
      readPackPaginationParamNames(optionsConfig["pagination"]),
    );

    return this.callWorkflowProxy({
      endpoint,
      method,
      query: usesPackProxyRequestBody(method) ? undefined : query,
      body,
      headers: sanitizeHeaders(optionsConfig["headers"]),
    });
  }

  async fetchOptionsPage(
    optionsConfig: Record<string, unknown> | null | undefined,
    vars: PackDataSourceVars = {},
    page = 1,
    limit = 10,
    q?: string,
  ): Promise<PackFetchOptionsPageResult> {
    if (!optionsConfig) {
      return emptyPackOptionsPage();
    }

    const staticOpts = Array.isArray(optionsConfig["options"])
      ? (optionsConfig["options"] as Array<{
          label?: string;
          value?: string;
          active?: boolean;
        }>)
      : null;
    if (staticOpts?.length || optionsConfig["source"] === "static") {
      const options = (staticOpts ?? [])
        .filter((o) => o.active !== false)
        .map((o) => ({
          label: String(o.label ?? o.value ?? ""),
          value: String(o.value ?? o.label ?? ""),
        }));
      return {
        options,
        attributesByValue: {},
        metadata: {
          total: options.length,
          page: 1,
          limit: options.length || limit,
          totalPages: 1,
          hasMore: false,
        },
      };
    }

    const resolvedVars = this.enrichPackDataSourceVars(vars);
    const rawEndpoint = coerceText(optionsConfig["endpoint"] ?? "");
    let endpoint = normalizePackProxyEndpoint(rawEndpoint);
    endpoint = normalizePackSourceTypeEndpoint(rawEndpoint, endpoint);
    if (!endpoint) {
      return emptyPackOptionsPage();
    }
    if (shouldSkipSourceDescriptionsForDirect(endpoint, resolvedVars)) {
      return emptyPackOptionsPage();
    }

    const method = coerceText(optionsConfig["method"] ?? "GET");
    const jsonQuery = {
      ...extractPackEndpointQuery(rawEndpoint),
      ...buildPackQuery(optionsConfig["query"], resolvedVars),
    };
    const pageLimit = readPackQueryLimit(optionsConfig["query"], limit);
    const paramNames = readPackPaginationParamNames(
      optionsConfig["pagination"],
    );
    const query: Record<string, string> = {
      ...jsonQuery,
      [paramNames.page]: String(page),
      [paramNames.pageSize]: String(pageLimit),
    };
    if (q?.trim()) {
      query[paramNames.search] = q.trim();
    }
    normalizePackSourceDescriptionQuery(endpoint, query);

    const mapping = normalizePackOptionsResponseMapping(
      rawEndpoint,
      (optionsConfig["responseMapping"] as Record<string, unknown>) ?? {},
    );

    const body = buildPackOptionsProxyBody(
      optionsConfig,
      query,
      paramNames,
    );

    const raw = await this.callWorkflowProxy({
      endpoint,
      method,
      query: usesPackProxyRequestBody(method) ? undefined : query,
      body,
      headers: sanitizeHeaders(optionsConfig["headers"]),
    });
    const mapped = extractMappedOptionsWithAttributes(raw, mapping);
    const metadata = extractPaginationMetadata(raw, mapped.options.length);
    return { ...mapped, metadata };
  }

  extractCollection(
    raw: unknown,
    responseMapping?: Record<string, unknown> | null,
  ): Record<string, unknown>[] {
    const path =
      typeof responseMapping?.["collectionPath"] === "string"
        ? responseMapping["collectionPath"]
        : "data";
    return extractPaginatedCollection(raw, path) as Record<string, unknown>[];
  }

  extractTotal(
    raw: unknown,
    responseMapping?: Record<string, unknown> | null,
  ): number | null {
    const path =
      typeof responseMapping?.["totalPath"] === "string"
        ? responseMapping["totalPath"]
        : "metadata.total";
    for (const candidate of [
      path,
      "data.total",
      "metadata.total",
      "total",
      "data.metadata.total",
    ]) {
      const value = getByPath(raw, candidate);
      if (typeof value === "number") return value;
    }
    const items = extractPaginatedCollection(
      raw,
      coerceText(responseMapping?.["collectionPath"] ?? "data"),
    );
    return items.length > 0 ? items.length : null;
  }

  extractRecord(
    raw: unknown,
    responseMapping?: Record<string, unknown> | null,
  ): Record<string, unknown> | null {
    const path =
      typeof responseMapping?.["recordPath"] === "string"
        ? responseMapping["recordPath"]
        : "data";
    for (const candidate of [path, "data", "record"]) {
      const value = getByPath(raw, candidate);
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return value as Record<string, unknown>;
      }
    }
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      return raw as Record<string, unknown>;
    }
    return null;
  }
}

/** Pack manifest `/masters/*` paths → LOS reference-data routes. */
const MASTERS_PACK_TO_REFERENCE_DATA: Record<string, string> = {
  "/masters/products": "/reference-data/masters/product",
  "/masters/sources": "/reference-data/masters/sourceType",
};

const REFERENCE_DATA_PATH_ALIASES: Record<string, string> = {
  "/reference-data/sourceType": "/reference-data/masters/sourceType",
  "/reference-data/source": "/reference-data/masters/sourceType",
};

/**
 * Normalize a pack JSON endpoint to a relative path for the workflow proxy
 * (strip host + `/api/v1`). Does not remap `/masters/*` — the backend proxy
 * rewrites those to LOS `/reference-data/*` routes.
 */
export function normalizePackProxyEndpoint(endpoint: string): string {
  if (!endpoint) return "";

  let resolved = endpoint;

  if (/^https?:\/\//i.test(endpoint)) {
    try {
      const url = new URL(endpoint);
      if (/^https?:\/\/api\.example\.com/i.test(endpoint)) {
        let path = url.pathname || "/";
        if (/\/customers\/search\/?$/i.test(path)) path = "/parties";
        resolved = path;
      } else {
        resolved = url.pathname;
      }
    } catch {
      return endpoint;
    }
  }

  return stripLosApiVersionPrefix(resolved);
}

/** Block proxy calls when workflow template variables were not substituted. */
export function assertResolvedPackEndpoint(endpoint: string): void {
  if (endpoint.includes('{{')) {
    throw new Error(`Unresolved workflow endpoint template: ${endpoint}`);
  }
}

/** Rewrite pack endpoints to LOS-relative paths for direct client calls (submit/detail). */
export function resolvePackEndpoint(endpoint: string): string {
  const normalized = normalizePackProxyEndpoint(endpoint);
  return (
    MASTERS_PACK_TO_REFERENCE_DATA[normalized] ??
    REFERENCE_DATA_PATH_ALIASES[normalized] ??
    normalized
  );
}

/**
 * `apiUrl` / Nest proxy base already includes `/api/v1`; pack JSON often uses
 * absolute URLs whose pathname repeats that prefix — strip it so we call
 * `/opportunities/...` not `/api/v1/opportunities/...`.
 */
export function stripLosApiVersionPrefix(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const apiVersionPrefix = /^\/api\/v\d+(?=\/|$)/i;
  const match = apiVersionPrefix.exec(normalized);
  if (!match) {
    return normalized;
  }
  const stripped = normalized.slice(match[0].length);
  return stripped || "/";
}

/**
 * Some pack JSON authors embed query params directly on the endpoint URL
 * (e.g. ".../generic-masters?limit=20") instead of the separate `query`
 * config. `resolvePackEndpoint` strips those (LOS proxy only forwards the
 * path), so recover them here to merge into the request query — lowest
 * priority so explicit `query` config and pagination params still win.
 */
export function extractPackEndpointQuery(
  endpoint: string,
): Record<string, string> {
  if (!endpoint || !/^https?:\/\//i.test(endpoint)) return {};
  try {
    const url = new URL(endpoint);
    const out: Record<string, string> = {};
    url.searchParams.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  } catch {
    return {};
  }
}

export function buildPackQuery(
  raw: unknown,
  vars: PackDataSourceVars,
): Record<string, string> {
  const out: Record<string, string> = {};
  const replace = (value: string) =>
    value.replace(/\{\{(\w+)\}\}/g, (_m, key: string) =>
      vars[key] != null && vars[key] !== "" ? String(vars[key]) : "",
    );

  if (Array.isArray(raw)) {
    for (const item of raw as PackQueryItem[]) {
      // enabled:false marks optional search params — still send when vars resolve.
      addPackQueryEntry(
        out,
        item?.key,
        String(item?.value ?? ""),
        replace,
        vars,
      );
    }
    return out;
  }

  if (raw && typeof raw === "object") {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      addPackQueryEntry(out, key, coerceText(value ?? ""), replace, vars);
    }
  }
  return out;
}

function addPackQueryEntry(
  out: Record<string, string>,
  key: string | undefined,
  value: string,
  replace: (value: string) => string,
  vars: PackDataSourceVars,
): void {
  if (!key) return;
  const normalizedKey = key === "pageSize" ? "limit" : key;
  let resolved = replace(value);
  if (resolved === "") {
    resolved =
      resolvePackQueryVar(vars, normalizedKey) ??
      resolvePackQueryVar(vars, key) ??
      "";
  }
  if (resolved !== "") {
    out[normalizedKey] = resolved;
  }
}

function resolvePackQueryVar(
  vars: PackDataSourceVars,
  key: string,
): string | undefined {
  const value = vars[key];
  if (value == null || value === "") {
    return undefined;
  }
  return String(value);
}

export function readPackQueryLimit(raw: unknown, fallback = 10): number {
  const built = buildPackQuery(raw, {});
  const parsed = Number(built["limit"]);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return fallback;
}

export type PackPaginationParamNames = {
  page: string;
  pageSize: string;
  search: string;
};

/**
 * Read the query param names an options endpoint expects for page/pageSize/search
 * from `optionsConfig.pagination.paramNames`, falling back to the historical
 * `page`/`limit`/`q` defaults when the field doesn't declare an explicit contract.
 */
function usesPackProxyRequestBody(method: string): boolean {
  const verb = method.toUpperCase();
  return verb === "POST" || verb === "PUT" || verb === "PATCH";
}

function buildPackOptionsProxyBody(
  optionsConfig: Record<string, unknown>,
  query: Record<string, string>,
  paramNames: PackPaginationParamNames,
): unknown {
  const method = coerceText(optionsConfig["method"] ?? "GET");
  if (!usesPackProxyRequestBody(method)) {
    return undefined;
  }

  const base = optionsConfig["body"];
  const body: Record<string, unknown> =
    base && typeof base === "object" && !Array.isArray(base)
      ? { ...(base as Record<string, unknown>) }
      : {};

  const pageRaw = query[paramNames.page];
  if (pageRaw) {
    const page = Number(pageRaw);
    if (!Number.isNaN(page)) {
      body["page"] = page;
    }
  }
  const limitRaw = query[paramNames.pageSize];
  if (limitRaw) {
    const limit = Number(limitRaw);
    if (!Number.isNaN(limit)) {
      body["limit"] = limit;
    }
  }
  const searchRaw = query[paramNames.search]?.trim();
  if (searchRaw) {
    body["search"] = searchRaw;
  }

  return body;
}

export function readPackPaginationParamNames(
  raw: unknown,
): PackPaginationParamNames {
  const pagination = (raw ?? {}) as Record<string, unknown>;
  const paramNames = (pagination["paramNames"] ?? {}) as Record<
    string,
    unknown
  >;
  return {
    page:
      typeof paramNames["page"] === "string" && paramNames["page"]
        ? paramNames["page"]
        : "page",
    pageSize:
      typeof paramNames["pageSize"] === "string" && paramNames["pageSize"]
        ? paramNames["pageSize"]
        : "limit",
    search:
      typeof paramNames["search"] === "string" && paramNames["search"]
        ? paramNames["search"]
        : "q",
  };
}

/** Legacy pack forms call `/masters/sources`; LMS serves vendor types via generic-masters. */
function normalizePackSourceTypeEndpoint(
  rawEndpoint: string,
  endpoint: string,
): string {
  if (
    /\/masters\/sources(?:\/|\?|$)/i.test(rawEndpoint) ||
    endpoint === "/masters/sources"
  ) {
    return "/masters/generic-masters";
  }
  return endpoint;
}

/**
 * Branch masters return `{ code, label }`. Pack JSON may swap labelKey/valueKey.
 * Legacy `/masters/sources` is served from generic-masters `VENDORTYPE`.
 */
function normalizePackOptionsResponseMapping(
  endpoint: string,
  mapping: Record<string, unknown>,
): Record<string, unknown> {
  if (/\/masters\/sources(?:\/|\?|$)/i.test(endpoint)) {
    return {
      ...mapping,
      collectionPath: "data.VENDORTYPE",
      labelKey:
        coerceText(mapping["labelKey"]).toLowerCase() === "label"
          ? "description"
          : coerceText(mapping["labelKey"]) || "description",
      valueKey: coerceText(mapping["valueKey"]) || "code",
    };
  }
  if (!/\/branches(?:\/|\?|$)/i.test(endpoint)) {
    return mapping;
  }
  const labelKey = coerceText(mapping["labelKey"]).toLowerCase();
  const valueKey = coerceText(mapping["valueKey"]).toLowerCase();
  if (labelKey === "code" && (valueKey === "label" || valueKey === "name")) {
    return { ...mapping, labelKey: "label", valueKey: "code" };
  }
  return {
    ...mapping,
    labelKey: coerceText(mapping["labelKey"]) || "label",
    valueKey: coerceText(mapping["valueKey"]) || "code",
  };
}

function shouldSkipSourceDescriptionsForDirect(
  endpoint: string,
  vars: PackDataSourceVars,
): boolean {
  if (!endpoint.includes("source-descriptions")) {
    return false;
  }
  return Object.values(vars).some((value) => isDirectSourceType(value));
}

function normalizePackSourceDescriptionQuery(
  endpoint: string,
  query: Record<string, string>,
): void {
  if (!endpoint.includes("source-descriptions")) {
    return;
  }
  // Pack JSON / LMS masters API expects `sourceCode`; keep it for `/masters/*` proxy calls.
  if (endpoint.includes("/masters/")) {
    const sourceCode = query["sourceCode"] ?? query["sourceTypeCode"];
    if (sourceCode) {
      query["sourceCode"] = sourceCode;
    }
    delete query["sourceTypeCode"];
    return;
  }
  // LOS reference-data BFF expects `sourceTypeCode`.
  if (query["sourceCode"] && !query["sourceTypeCode"]) {
    query["sourceTypeCode"] = query["sourceCode"];
  }
  delete query["sourceCode"];
}

function sanitizeHeaders(headers: unknown): Record<string, string> | undefined {
  return normalizePackHeaders(headers);
}

/** Normalize workflow header config (array or object); strips sensitive client headers. */
export function normalizePackHeaders(headers: unknown): Record<string, string> | undefined {
  const out: Record<string, string> = {};

  if (Array.isArray(headers)) {
    mergePackHeaderArray(out, headers);
  } else if (headers && typeof headers === 'object') {
    mergePackHeaderObject(out, headers as Record<string, unknown>);
  }

  return Object.keys(out).length ? out : undefined;
}

function mergePackHeaderArray(out: Record<string, string>, headers: unknown[]): void {
  for (const item of headers) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const record = item as Record<string, unknown>;
    const key = coerceText(record['key']);
    const value = coerceText(record['value']);
    if (key && value && !isSensitiveClientHeader(key)) {
      out[key] = value;
    }
  }
}

function mergePackHeaderObject(
  out: Record<string, string>,
  headers: Record<string, unknown>,
): void {
  for (const [key, value] of Object.entries(headers)) {
    if (isSensitiveClientHeader(key)) {
      continue;
    }
    if (value != null && value !== '') {
      out[key] = coerceText(value);
    }
  }
}

function isSensitiveClientHeader(key: string): boolean {
  return /^(authorization|cookie|x-api-key|api[-_]?key)$/i.test(key);
}

function emptyPackOptionsPage(): PackFetchOptionsPageResult {
  return {
    options: [],
    attributesByValue: {},
    metadata: {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
      hasMore: false,
    },
  };
}

function extractMappedOptionsWithAttributes(
  raw: unknown,
  mapping: Record<string, unknown>,
): PackFetchOptionsResult {
  const collectionPath = coerceText(mapping["collectionPath"] ?? "data");
  const labelKey = coerceText(mapping["labelKey"] ?? "label");
  const valueKey = coerceText(mapping["valueKey"] ?? "code");
  const attributeMappings = Array.isArray(mapping["attributes"])
    ? (mapping["attributes"] as Array<{
        sourceKey?: string;
        targetKey?: string;
        writeToField?: string;
      }>)
    : [];

  const cursor = extractPaginatedCollection(raw, collectionPath);
  if (!cursor.length) return { options: [], attributesByValue: {} };

  const attributesByValue: Record<string, Record<string, unknown>> = {};
  const options = cursor.map((row) => {
    const r = row as Record<string, unknown>;
    const value = coerceText(
      r[valueKey] ?? r["code"] ?? r["id"] ?? r[labelKey] ?? "",
    );
    const rowLabel = coerceText(
      r[labelKey] ??
        r["label"] ??
        r["name"] ??
        r["description"] ??
        r[valueKey] ??
        "",
    );
    // Always carry the row's label into attrs — it's the only way to bridge a
    // selected value to its attributes when the value itself isn't on the
    // currently-loaded page (e.g. edit-mode seed by label instead of by key).
    const attrs: Record<string, unknown> = rowLabel ? { label: rowLabel } : {};
    for (const attr of attributeMappings) {
      const target = attr.writeToField ?? attr.targetKey;
      if (!target) continue;
      const sourceKey = attr.sourceKey ?? attr.targetKey;
      if (!sourceKey) continue;
      const resolved = resolvePackAttributeValue(r, sourceKey);
      if (resolved !== undefined) {
        attrs[target] = resolved;
      }
    }
    if (value) attributesByValue[value] = attrs;
    return {
      label: coerceText(
        r[labelKey] ??
          r["label"] ??
          r["name"] ??
          r["description"] ??
          r[valueKey] ??
          "",
      ),
      value,
    };
  });

  return { options, attributesByValue };
}

function unwrapParsedValue(value: unknown): unknown {
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "parsedValue" in value
  ) {
    return (value as { parsedValue: unknown }).parsedValue;
  }
  return value;
}

function resolvePackAttributeValue(
  row: Record<string, unknown>,
  sourceKey: string,
): unknown {
  const direct = unwrapParsedValue(getByPath(row, sourceKey) ?? row[sourceKey]);
  if (direct !== undefined && direct !== null && direct !== "") {
    return direct;
  }
  const withoutParsed = sourceKey.replace(/\.parsedValue$/i, "");
  if (withoutParsed !== sourceKey) {
    return unwrapParsedValue(
      getByPath(row, withoutParsed) ?? row[withoutParsed],
    );
  }
  return direct;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: string): boolean {
  return UUID_PATTERN.test(value);
}

function getByPath(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== "object") return undefined;
  let cur: unknown = obj;
  for (const part of path
    .replace(/^\$\.?/, "")
    .split(".")
    .filter(Boolean)) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

export function getValueByPath(obj: unknown, path: string | undefined): unknown {
  if (!path?.trim()) {
    return undefined;
  }
  return getByPath(obj, path.trim());
}

/**
 * Map a single master lookup response (e.g. GET /masters/bplr-value?code=…) into
 * option attributes for `writeToField` patching on the driving select field.
 */
export function mapPackMasterLookupAttributes(
  raw: unknown,
  mapping: Record<string, unknown>,
  selectedValue: string,
): Record<string, unknown> | null {
  const recordPath = coerceText(mapping["recordPath"] ?? "data");
  const payload = unwrapApiPayload(raw);
  let row: unknown = recordPath
    ? getByPath(payload, recordPath)
    : payload;
  if (
    (row == null || typeof row !== "object" || Array.isArray(row)) &&
    recordPath === "data" &&
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload)
  ) {
    row = payload;
  }
  if (!row || typeof row !== "object" || Array.isArray(row)) {
    return null;
  }

  const mapped = extractMappedOptionsWithAttributes(
    { data: [row as Record<string, unknown>] },
    { ...mapping, collectionPath: "data" },
  );
  const valueKey = coerceText(mapping["valueKey"] ?? "code");
  const rowCode = coerceText((row as Record<string, unknown>)[valueKey]);
  const attrs =
    mapped.attributesByValue[selectedValue] ??
    (rowCode ? mapped.attributesByValue[rowCode] : undefined) ??
    Object.values(mapped.attributesByValue)[0];

  return attrs && Object.keys(attrs).length ? attrs : null;
}
